import React from 'react';
import { useAccessibility, hasModuleAccess, hasPermission, canAdd, canView, canEdit, canApprove, canReject, canCancel } from '../../utils/accessControl';

interface AccessControlProps {
  children: React.ReactNode;
  moduleName: string;
  permission?: 'add' | 'view' | 'edit' | 'approve' | 'reject' | 'cancel';
  fallback?: React.ReactNode;
}

/**
 * Component that renders children only if user has access to the specified module
 */
export const ModuleAccess: React.FC<AccessControlProps> = ({ 
  children, 
  moduleName, 
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const hasAccess = hasModuleAccess(accessibility, moduleName);
  
  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders children only if user has specific permission for the module
 */
export const PermissionAccess: React.FC<AccessControlProps> = ({ 
  children, 
  moduleName, 
  permission = 'view',
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const hasPermissionAccess = hasPermission(accessibility, moduleName, permission);
  
  return hasPermissionAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders children only if user can add items in the module
 */
export const AddAccess: React.FC<Omit<AccessControlProps, 'permission'>> = ({ 
  children, 
  moduleName, 
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const canAddItems = canAdd(accessibility, moduleName);
  
  return canAddItems ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders children only if user can view items in the module
 */
export const ViewAccess: React.FC<Omit<AccessControlProps, 'permission'>> = ({ 
  children, 
  moduleName, 
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const canViewItems = canView(accessibility, moduleName);
  
  return canViewItems ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders children only if user can edit items in the module
 */
export const EditAccess: React.FC<Omit<AccessControlProps, 'permission'>> = ({ 
  children, 
  moduleName, 
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const canEditItems = canEdit(accessibility, moduleName);
  
  return canEditItems ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders children only if user can approve items in the module
 */
export const ApproveAccess: React.FC<Omit<AccessControlProps, 'permission'>> = ({ 
  children, 
  moduleName, 
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const canApproveItems = canApprove(accessibility, moduleName);
  
  return canApproveItems ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders children only if user can reject items in the module
 */
export const RejectAccess: React.FC<Omit<AccessControlProps, 'permission'>> = ({ 
  children, 
  moduleName, 
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const canRejectItems = canReject(accessibility, moduleName);
  
  return canRejectItems ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders children only if user can cancel items in the module
 */
export const CancelAccess: React.FC<Omit<AccessControlProps, 'permission'>> = ({ 
  children, 
  moduleName, 
  fallback = null 
}) => {
  const accessibility = useAccessibility();
  const canCancelItems = canCancel(accessibility, moduleName);
  
  return canCancelItems ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that renders different content based on user's permissions
 */
interface ConditionalAccessProps {
  moduleName: string;
  viewContent?: React.ReactNode;
  addContent?: React.ReactNode;
  editContent?: React.ReactNode;
  approveContent?: React.ReactNode;
  rejectContent?: React.ReactNode;
  cancelContent?: React.ReactNode;
  noAccessContent?: React.ReactNode;
}

export const ConditionalAccess: React.FC<ConditionalAccessProps> = ({
  moduleName,
  viewContent,
  addContent,
  editContent,
  approveContent,
  rejectContent,
  cancelContent,
  noAccessContent = <div>No access available</div>
}) => {
  const accessibility = useAccessibility();
  
  if (!hasModuleAccess(accessibility, moduleName)) {
    return <>{noAccessContent}</>;
  }
  
  // Check permissions in order of priority
  if (canAdd(accessibility, moduleName) && addContent) {
    return <>{addContent}</>;
  }
  
  if (canEdit(accessibility, moduleName) && editContent) {
    return <>{editContent}</>;
  }
  
  if (canApprove(accessibility, moduleName) && approveContent) {
    return <>{approveContent}</>;
  }
  
  if (canReject(accessibility, moduleName) && rejectContent) {
    return <>{rejectContent}</>;
  }
  
  if (canCancel(accessibility, moduleName) && cancelContent) {
    return <>{cancelContent}</>;
  }
  
  if (canView(accessibility, moduleName) && viewContent) {
    return <>{viewContent}</>;
  }
  
  return <>{noAccessContent}</>;
};

export default {
  ModuleAccess,
  PermissionAccess,
  AddAccess,
  ViewAccess,
  EditAccess,
  ApproveAccess,
  RejectAccess,
  CancelAccess,
  ConditionalAccess
}; 